import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Phone, CheckCircle2, MessageCircle } from "lucide-react";
import * as Icons from "lucide-react";
import SEO from "../components/SEO";
import AppointmentDialog from "../components/AppointmentDialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { getServiceBySlug, SERVICES } from "../data/services";
import { SITE, callLink, whatsappLink } from "../lib/constants";
import useReveal from "../hooks/useReveal";

export default function ServiceDetail() {
  useReveal();
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Sparkles;

  const faqStructured = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const relatedServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      <SEO
        title={service.seoTitle}
        description={service.metaDescription}
        keywords={`${service.name.toLowerCase()} allahabad, ${service.name.toLowerCase()} prayagraj, dental treatment`}
        canonical={`https://lotusfamilydental.me/services/${service.slug}`}
        image={service.coverImage}
        structuredData={faqStructured}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-teal-100/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-12 lg:pb-20">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-medium mb-5">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {service.name}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 leading-[1.05]">
                {service.h1}
              </h1>
              <p className="mt-4 text-lg text-teal-700 font-medium">{service.tagline}</p>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">{service.intro}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => setDialogOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 shadow-[0_8px_30px_-4px_rgba(13,148,136,0.4)]"
                  data-testid="service-book-button"
                >
                  Book Appointment <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href={callLink}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50"
                >
                  <Phone className="w-4 h-4 text-teal-600" /> {SITE.phone}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden bg-slate-100 shadow-2xl shadow-slate-300/30">
                <img src={service.coverImage} alt={service.name} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SYMPTOMS + BENEFITS GRID */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <div className="reveal bg-slate-50 rounded-3xl p-8 lg:p-10">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Symptoms
            </div>
            <h2 className="font-heading text-2xl lg:text-3xl font-medium text-slate-900">
              Do you experience any of these?
            </h2>
            <ul className="mt-6 space-y-3">
              {service.symptoms.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2.5 flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal bg-white rounded-3xl border border-slate-100 p-8 lg:p-10">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Why us
            </div>
            <h2 className="font-heading text-2xl lg:text-3xl font-medium text-slate-900">
              Benefits at Lotus Family Dental
            </h2>
            <ul className="mt-6 space-y-3">
              {service.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Treatment process
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              How it <span className="text-teal-700 font-medium">works.</span>
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              A simple step-by-step plan designed for comfort and clarity.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.process.map((step, i) => (
              <div
                key={i}
                className="reveal bg-white rounded-3xl p-7 border border-slate-100 relative premium-card"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="absolute -top-3 left-7 px-3 py-1 bg-teal-600 text-white text-xs font-medium rounded-full">
                  Step {i + 1}
                </div>
                <h3 className="font-heading text-lg font-medium text-slate-900 mt-2">{step.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Common questions
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              You asked. We answer.
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {service.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border border-slate-100 rounded-2xl px-5 sm:px-6"
              >
                <AccordionTrigger className="text-left text-base font-medium text-slate-900 py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 p-8 lg:p-14 text-white">
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-3xl lg:text-4xl font-light tracking-tight">
                  Book your {service.name.toLowerCase()} consultation today.
                </h2>
                <p className="mt-3 text-teal-50">
                  Free first consultation. Same-day appointments available.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <button
                  onClick={() => setDialogOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-teal-700 font-medium hover:bg-teal-50"
                  data-testid="service-bottom-book"
                >
                  Book Now <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href={whatsappLink(`Hi, I want to know more about ${service.name}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-transparent border border-white/30 text-white font-medium hover:bg-white/10"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading text-2xl font-medium text-slate-900">Related Treatments</h3>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {relatedServices.map((s) => {
              const RIcon = Icons[s.icon] || Icons.Sparkles;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="bg-white border border-slate-100 rounded-2xl p-5 hover:border-teal-200 hover:shadow-md transition-all flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                    <RIcon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.tagline}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <AppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultService={service.name} />
    </>
  );
}
